import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import logger from '../utils/logger';
import { SUBTITLE_LANGUAGE_CODES, SUBTITLE_LANG_LABELS } from '../constants/subtitleLanguages';
import { subtitleDebug, logTextTracks } from '../utils/subtitleDebug';

export { SUBTITLE_LANG_LABELS, SUBTITLE_LANGUAGE_CODES } from '../constants/subtitleLanguages';

/** True while Safari is AirPlaying this video to a TV. */
export const isAirPlaying = (video) => Boolean(video && video.webkitCurrentPlaybackTargetIsWireless);

/** 'en' matches 'en', 'en-US', 'EN'. */
export const sameLanguage = (a, b) =>
  Boolean(a && b) && String(a).toLowerCase().split('-')[0] === String(b).toLowerCase().split('-')[0];

export function buildSubtitleMap(subtitleUrl, subtitleUrls) {
  if (subtitleUrls && typeof subtitleUrls === 'object') return subtitleUrls;
  if (subtitleUrl) return { en: subtitleUrl };
  return {};
}

export function useSubtitleControls(
  videoRef,
  subtitleUrls,
  { enabled = true, requestSubtitle, loadingLang, nativeCaptions = true } = {}
) {
  const subtitleMap = useMemo(
    () => buildSubtitleMap(null, subtitleUrls || {}),
    [subtitleUrls]
  );

  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');
  const [showCCMenu, setShowCCMenu] = useState(false);
  const [activeCueText, setActiveCueText] = useState(''); // for custom (non-native) caption rendering
  const [airplaying, setAirplaying] = useState(false); // captions are on the TV while AirPlaying
  const ccMenuRef = useRef(null);
  const applyCountRef = useRef(0);

  useEffect(() => {
    subtitleDebug('useSubtitleControls', 'subtitlesEnabled changed', { subtitlesEnabled });
  }, [subtitlesEnabled]);

  useEffect(() => {
    subtitleDebug('useSubtitleControls', 'selectedLang changed', { selectedLang });
  }, [selectedLang]);

  useEffect(() => {
    subtitleDebug('useSubtitleControls', 'subtitleMap changed', {
      langs: Object.keys(subtitleMap || {}),
      urls: subtitleMap,
    });
  }, [subtitleMap]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return undefined;

    const applySubtitleMode = () => {
      const tracks = video.textTracks;
      applyCountRef.current += 1;
      const runId = applyCountRef.current;

      subtitleDebug('useSubtitleControls', `applySubtitleMode #${runId} START`, {
        subtitlesEnabled,
        selectedLang,
        trackCount: tracks.length,
      });
      logTextTracks(video, `applySubtitleMode #${runId} BEFORE`);

      // Only tracks from our own <track> elements are ours to show. A Mux
      // stream played natively (Safari) also exposes its in-stream captions
      // as text tracks; showing those too would draw every line twice.
      //
      // AirPlay is the exception: the TV only receives the stream, never our
      // <track> files or on-page captions. While AirPlaying, the in-stream
      // track for the chosen language is what shows (on the TV), and ours
      // step aside.
      const ownTracks = new Set(
        Array.from(video.querySelectorAll('track')).map((el) => el.track)
      );
      const onTv = isAirPlaying(video);
      let showingTrack = null;
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        if (!ownTracks.has(track)) {
          const tvMode =
            onTv && subtitlesEnabled && sameLanguage(track.language, selectedLang) ? 'showing' : 'disabled';
          if (track.mode !== tvMode) track.mode = tvMode;
          continue;
        }
        if (onTv) {
          if (track.mode !== 'disabled') track.mode = 'disabled';
          continue;
        }
        // For custom caption rendering we use 'hidden' (cues still parse + fire
        // cuechange, but the browser doesn't draw its own ::cue — we draw our own).
        const showMode = nativeCaptions ? 'showing' : 'hidden';
        const nextMode =
          subtitlesEnabled && track.language === selectedLang ? showMode : 'hidden';

        if (track.mode !== nextMode) {
          subtitleDebug('useSubtitleControls', `applySubtitleMode #${runId} set track[${i}]`, {
            language: track.language,
            label: track.label,
            from: track.mode,
            to: nextMode,
          });
          track.mode = nextMode;
        }

        if (nextMode === 'showing') {
          showingTrack = { index: i, language: track.language, label: track.label };
        }
      }

      subtitleDebug('useSubtitleControls', `applySubtitleMode #${runId} END`, {
        showingTrack: showingTrack || 'none',
        subtitlesEnabled,
        selectedLang,
      });
      logTextTracks(video, `applySubtitleMode #${runId} AFTER`);

      logger.log(
        `🔤 textTracks: ${tracks.length}, enabled: ${subtitlesEnabled}, lang: ${selectedLang}`
      );
    };

    applySubtitleMode();
    video.addEventListener('loadedmetadata', applySubtitleMode);
    video.textTracks.addEventListener('addtrack', applySubtitleMode);
    // Safari fires this when AirPlay starts or stops.
    video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', applySubtitleMode);

    return () => {
      video.removeEventListener('loadedmetadata', applySubtitleMode);
      video.textTracks.removeEventListener('addtrack', applySubtitleMode);
      video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', applySubtitleMode);
    };
  }, [videoRef, subtitleMap, subtitlesEnabled, selectedLang, enabled, nativeCaptions]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return undefined;
    const sync = () => setAirplaying(isAirPlaying(video));
    sync();
    video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', sync);
    return () => video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', sync);
  }, [videoRef, enabled]);

  // Track the active cue text for the selected language so consumers can render
  // captions in their own DOM (sized to the frame) instead of the browser's ::cue.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled || nativeCaptions || !subtitlesEnabled || airplaying) {
      setActiveCueText('');
      return undefined;
    }
    let track = null;
    for (const el of Array.from(video.querySelectorAll('track'))) {
      if (el.track && el.track.language === selectedLang) track = el.track;
    }
    if (!track) {
      setActiveCueText('');
      return undefined;
    }
    const onCueChange = () => {
      const cues = track.activeCues;
      setActiveCueText(
        cues && cues.length
          ? Array.from(cues).map((c) => c.text).join('\n')
          : ''
      );
    };
    track.addEventListener('cuechange', onCueChange);
    onCueChange();
    return () => track.removeEventListener('cuechange', onCueChange);
  }, [videoRef, subtitleMap, selectedLang, subtitlesEnabled, enabled, nativeCaptions, airplaying]);

  useEffect(() => {
    if (!showCCMenu) return undefined;
    const handleClickOutside = (e) => {
      if (ccMenuRef.current && !ccMenuRef.current.contains(e.target)) {
        setShowCCMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCCMenu]);

  const selectLanguage = useCallback(
    async (lang) => {
      const code = (lang || 'en').toLowerCase();
      subtitleDebug('useSubtitleControls', 'selectLanguage (CC menu)', {
        code,
        hadUrl: Boolean(subtitleMap[code]),
        subtitlesEnabled,
      });

      setSelectedLang(code);
      setSubtitlesEnabled(true);
      setShowCCMenu(false);

      if (!subtitleMap[code] && requestSubtitle) {
        subtitleDebug('useSubtitleControls', 'selectLanguage → requestSubtitle', { code });
        await requestSubtitle(code);

        // Some browsers auto-pause when a freshly-generated cross-origin <track>
        // attaches. Undo ONLY that single auto-pause, and only if the video is still
        // playing at the moment generation finished — so a manual pause made during
        // the wait (or afterward) is respected rather than overridden.
        const video = videoRef.current;
        if (video && !video.paused) {
          let handled = false;
          const undoAutoPause = () => {
            if (handled) return;
            handled = true;
            video.removeEventListener('pause', undoAutoPause);
            if (video.paused) video.play().catch(() => {});
          };
          video.addEventListener('pause', undoAutoPause);
          // If no auto-pause happens shortly, stop listening — never touch later pauses.
          setTimeout(() => {
            handled = true;
            video.removeEventListener('pause', undoAutoPause);
          }, 800);
        }
      } else {
        subtitleDebug('useSubtitleControls', 'selectLanguage → cached URL', {
          code,
          url: subtitleMap[code],
        });
      }
    },
    [requestSubtitle, subtitleMap, subtitlesEnabled, videoRef]
  );

  return {
    subtitleMap,
    availableLanguages: SUBTITLE_LANGUAGE_CODES,
    subtitlesEnabled,
    setSubtitlesEnabled,
    selectedLang,
    setSelectedLang,
    showCCMenu,
    setShowCCMenu,
    ccMenuRef,
    selectLanguage,
    loadingLang,
    activeCueText,
  };
}

export default useSubtitleControls;
